"use client";

import type { CSSProperties, ReactNode } from "react";
import { HOME_COPY } from "@/lib/landing-copy";
import { useId, useState } from "react";

import {
  ClosingSection,
  DATA_LABEL_CLS,
  DISPLAY,
  EDITORIAL_ACTION_CLS,
  EDITORIAL_CONTAINER_CLS,
  EDITORIAL_SECTION_CLS,
  GROUND,
  Grain,
  INK,
  LABEL_CLS,
  PARTNER_HREF,
  SECTION_TITLE_CLS,
  SectionMarker,
  SURFACE_DARK,
  WAITLIST_HREF,
} from "@/components/editorial";
import { type ConceptGroup, LIGHT_TOKENS } from "../spec";

/* ================================================================= */
/*  Shared idioms — lifted verbatim from WaitlistDialog / the home      */
/* ================================================================= */

const CONTACT_EMAIL = "team@mercurylabs.io";
const CONTACT_MAILTO = `mailto:${CONTACT_EMAIL}?subject=MLX%20%E2%80%94%20first%20activation`;

/** Field idiom from WaitlistDialog — hairline box, cream ground, claret focus. */
const INPUT_CLASS =
  "w-full border border-[var(--rule)] bg-transparent px-3 py-2.5 text-[0.95rem] text-[var(--theme-ink)] outline-none transition-colors placeholder:text-[color-mix(in_srgb,var(--theme-ink)_40%,transparent)] focus:border-[var(--theme-dark)]";

const FIELD_LABEL_CLASS = `${LABEL_CLS} text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]`;

const PRIMARY_ON_CREAM = `${EDITORIAL_ACTION_CLS} bg-[var(--theme-surface-dark)] text-[var(--theme-ground)] hover:bg-[color-mix(in_srgb,var(--theme-surface-dark)_88%,var(--theme-ink))] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-dark)]`;

const PRIMARY_ON_DARK = `${EDITORIAL_ACTION_CLS} bg-[var(--theme-ground)] text-[var(--theme-accent-text)] hover:bg-[color-mix(in_srgb,var(--theme-ground)_90%,var(--theme-accent))] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent)]`;

const OUTLINE_ON_DARK = `${EDITORIAL_ACTION_CLS} border border-[color-mix(in_srgb,var(--theme-ground)_38%,transparent)] text-[var(--theme-ground)] hover:bg-[color-mix(in_srgb,var(--theme-ground)_8%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent)]`;

const QUIET_LINK_CLASS =
  "text-[0.9rem] font-medium text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] underline-offset-4 transition-opacity hover:underline hover:opacity-80";

/** The dialog mounts outside the home's token wrapper on the live site, so
 *  the frame carries its own rule/label vars. Mirrors FRAME_STYLE there. */
const FRAME_STYLE: CSSProperties = {
  ...DISPLAY,
  ...LIGHT_TOKENS,
  backgroundColor: GROUND,
  color: INK,
};

/* ================================================================= */
/*  The exhibit frame shared by the enquiry form and the dialog         */
/* ================================================================= */

function EnquiryFrame({
  titleId,
  children,
  className = "",
  interactiveContact = true,
}: {
  titleId: string;
  children: ReactNode;
  className?: string;
  interactiveContact?: boolean;
}) {
  return (
    <section
      aria-labelledby={titleId}
      className={`w-full border border-[var(--rule)] ${className}`}
      style={FRAME_STYLE}
    >
      {/* Header row — DATA_LABEL pair over a dotted rule, inside the frame. */}
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-sm:flex-col max-sm:items-start max-sm:gap-1">
        <span
          id={titleId}
          className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
        >
          First activation · The AI Twin
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Scoped per activation
        </strong>
      </div>

      {children}

      {/* Footer row — the mailto stays visible so error copy has a referent. */}
      <div className="flex min-h-[2.2rem] flex-wrap items-center gap-2 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
        <span
          className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
        >
          Questions
        </span>
        {interactiveContact ? (
          <a
            href={CONTACT_MAILTO}
            className="text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[var(--theme-accent-text)] uppercase underline-offset-4 hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
        ) : (
          <span className="text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[var(--theme-accent-text)] uppercase">
            {CONTACT_EMAIL}
          </span>
        )}
      </div>
    </section>
  );
}

const ENQUIRY_FIELDS = [
  { label: "Full name" },
  { label: "Work email" },
  { label: "Company", suffix: " · optional" },
] as const;

function StaticEnquiryFormBody({ showClose = true }: { showClose?: boolean }) {
  return (
    <div className="px-5 py-6 md:px-7 md:py-7">
      <p className="max-w-[46ch] text-[0.98rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
        Tell us where you want AI to work better. We&apos;ll contact you to
        discuss a focused first activation.
      </p>

      <div className="mt-6 flex flex-col gap-5">
        {ENQUIRY_FIELDS.map((field) => (
          <div key={field.label} className="flex flex-col gap-2">
            <span className={FIELD_LABEL_CLASS}>
              {field.label}
              {"suffix" in field ? (
                <span className="text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]">
                  {field.suffix}
                </span>
              ) : null}
            </span>
            <span
              aria-hidden="true"
              className={`${INPUT_CLASS} block min-h-11`}
            />
          </div>
        ))}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <span className={PRIMARY_ON_CREAM}>Request a conversation</span>
        {showClose ? <span className={QUIET_LINK_CLASS}>Close</span> : null}
      </div>
    </div>
  );
}

type DialogPreviewState = "default" | "error" | "success";
const DIALOG_PREVIEW_STATES: DialogPreviewState[] = [
  "default",
  "error",
  "success",
];

function EnquiryFormBody({
  state,
  onSubmit,
  onClose,
}: {
  state: Exclude<DialogPreviewState, "success">;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const nameId = useId();
  const emailId = useId();
  const companyId = useId();
  const errorId = useId();
  const hasError = state === "error";

  return (
    <form
      className="px-5 py-6 md:px-7 md:py-7"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="max-w-[46ch] text-[0.98rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
        Tell us where you want AI to work better. We&apos;ll contact you to
        discuss a focused first activation.
      </p>

      {hasError ? (
        <p
          id={errorId}
          role="alert"
          className="mt-5 border-l-2 border-[var(--theme-dark)] pl-3 text-[0.9rem] leading-[1.5] text-[var(--theme-accent-text)]"
        >
          Check the work email and try again. Nothing has been sent.
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label htmlFor={nameId} className={FIELD_LABEL_CLASS}>
            Full name
          </label>
          <input
            id={nameId}
            name="name"
            type="text"
            required
            autoComplete="name"
            className={INPUT_CLASS}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor={emailId} className={FIELD_LABEL_CLASS}>
            Work email
          </label>
          <input
            id={emailId}
            name="email"
            type="email"
            required
            autoComplete="email"
            className={INPUT_CLASS}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            defaultValue={hasError ? "alex@" : undefined}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor={companyId} className={FIELD_LABEL_CLASS}>
            Company{" "}
            <span className="text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]">
              · optional
            </span>
          </label>
          <input
            id={companyId}
            name="company"
            type="text"
            autoComplete="organization"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <button type="submit" className={PRIMARY_ON_CREAM}>
          Request a conversation
        </button>
        <button type="button" className={QUIET_LINK_CLASS} onClick={onClose}>
          Close
        </button>
      </div>
    </form>
  );
}

function EnquirySuccessBody({ onClose }: { onClose?: () => void }) {
  return (
    <div className="px-5 py-6 md:px-7 md:py-8">
      <div className="flex items-center gap-3">
        <span className="size-2 shrink-0 bg-[var(--theme-dark)]" aria-hidden="true" />
        <span className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Status · Request received
        </span>
      </div>
      <p
        role="status"
        className="mt-5 max-w-[42ch] text-[1.05rem] leading-[1.55]"
        style={DISPLAY}
      >
        Thanks. We&apos;ll be in touch to discuss your first workflow and
        whether a focused activation is a good fit.
      </p>
      <div className="mt-7">
        {onClose ? (
          <button type="button" className={PRIMARY_ON_CREAM} onClick={onClose}>
            Close
          </button>
        ) : (
          <span className={PRIMARY_ON_CREAM}>Close</span>
        )}
      </div>
    </div>
  );
}

/* ================================================================= */
/*  CTA-001 · Closing section (live home content)                      */
/* ================================================================= */

function HomeClosing() {
  return (
    <ClosingSection
      content={{
        section: "07",
        label: "Your first activation",
        title: "Give your AI the business context it lacks.",
        body: "Choose a priority workflow, connect the systems behind it and review the first business context before approved AI puts it to work.",
        primaryCta: { label: "Discuss your first activation", href: WAITLIST_HREF },
        secondaryCta: { label: "Mercury Labs", href: "https://mercurylabs.io" },
        meta: [
          { label: "Product", value: "The AI Twin" },
          { label: "Deploy", value: "Managed targets" },
          { label: "Built by", value: "Mercury Labs · London" },
        ],
      }}
    />
  );
}

/* ================================================================= */
/*  CTA-002 · Inline enquiry form                                      */
/* ================================================================= */

function InlineEnquiryForm() {
  const titleId = useId();
  return (
    <EnquiryFrame titleId={titleId} interactiveContact={false}>
      <StaticEnquiryFormBody showClose={false} />
    </EnquiryFrame>
  );
}

/* ================================================================= */
/*  CTA-003 · Waitlist dialog, in-page replica                         */
/* ================================================================= */

function WaitlistDialogReplica() {
  const titleId = useId();
  const [state, setState] = useState<DialogPreviewState>("default");

  return (
    <div className="flex w-full flex-col items-center bg-black/60 p-4 py-[max(1rem,8vh)]">
      <div className="mb-3 flex w-full max-w-[34rem] flex-wrap items-center justify-between gap-2 border border-white/25 bg-black/45 px-3 py-2">
        <span className="text-[0.68rem] font-medium tracking-[0.14em] text-white/80 uppercase">
          Review state
        </span>
        <fieldset className="m-0 min-w-0 border-0 p-0">
          <legend className="sr-only">Dialog preview state</legend>
          <span className="flex flex-wrap gap-1.5">
            {DIALOG_PREVIEW_STATES.map((previewState) => {
              const active = state === previewState;
              return (
                <button
                  key={previewState}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setState(previewState)}
                  className="min-h-11 border px-3 text-[0.72rem] font-medium tracking-[0.1em] uppercase outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  style={{
                    borderColor: active ? GROUND : "rgba(255,255,255,0.3)",
                    backgroundColor: active ? GROUND : "transparent",
                    color: active ? INK : GROUND,
                  }}
                >
                  {previewState}
                </button>
              );
            })}
          </span>
        </fieldset>
      </div>
      <p className="sr-only" aria-live="polite">
        Dialog preview: {state}
      </p>
      <EnquiryFrame titleId={titleId} className="max-w-[34rem]">
        {state === "success" ? (
          <EnquirySuccessBody onClose={() => setState("default")} />
        ) : (
          <EnquiryFormBody
            key={state}
            state={state}
            onSubmit={() => setState("success")}
            onClose={() => setState("default")}
          />
        )}
      </EnquiryFrame>
    </div>
  );
}

/* ================================================================= */
/*  CTA-004 · Dialog success state                                     */
/* ================================================================= */

function WaitlistSuccessReplica() {
  const titleId = useId();
  return (
    <EnquiryFrame
      titleId={titleId}
      className="max-w-[34rem]"
      interactiveContact={false}
    >
      <EnquirySuccessBody />
    </EnquiryFrame>
  );
}

/* ================================================================= */
/*  CTA-005 · Sticky bar replica                                       */
/* ================================================================= */

function StickyBarReplica() {
  return (
    /* Cream stage: the bar is only py-3 tall, so without this the board's
       reference chip sits on top of the marker at narrow widths. */
    <div className="py-10 sm:py-12" style={{ backgroundColor: GROUND, color: INK }}>
      <div
        className="relative overflow-hidden border-y border-[var(--rule)]"
        style={{ backgroundColor: GROUND, color: INK }}
      >
        <Grain opacity={0.35} />
        <div
          className={`relative z-10 flex flex-wrap items-center gap-x-8 gap-y-3 py-3 md:py-3.5 ${EDITORIAL_CONTAINER_CLS}`}
        >
          <div className="flex shrink-0 items-center gap-3">
            <span className="size-2 bg-[var(--theme-dark)]" aria-hidden="true" />
            <span className={`text-[var(--theme-accent-text)] ${LABEL_CLS}`}>
              Your first activation
            </span>
          </div>
          <p
            className="min-w-0 flex-1 basis-[18rem] text-[0.98rem] font-medium leading-[1.4] tracking-[-0.005em] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]"
            style={DISPLAY}
          >
            Choose a priority workflow, connect the systems behind it.
          </p>
          <a href={WAITLIST_HREF} className={`${PRIMARY_ON_CREAM} ml-auto max-sm:w-full`}>
            Discuss your first activation
          </a>
        </div>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  CTA-006 · Dual CTA band                                            */
/* ================================================================= */

const DUAL_CELLS = [
  {
    marker: "Your first workflow",
    title: "Discuss your first workflow",
    body: "Show us a recurring report, an awkward handover or a decision that takes too long to prepare. We’ll explore where your Twin could help and agree a useful starting point.",
    href: WAITLIST_HREF,
    variant: "primary",
  },
  {
    marker: "Partnership",
    title: "Become a partner",
    body: "Bring the Twin to your clients. Talk to Mercury Labs, the team behind your Twin, about delivery and partnership options.",
    href: PARTNER_HREF,
    variant: "outline",
  },
] as const;

/** `title` defaults to the home closing line; the SKT concepts pass the
 *  same words with a hand-drawn mark. */
export function DualCtaBand({ title = HOME_COPY.closing }: { title?: ReactNode } = {}) {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.5} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section="" label="Two ways to start" dark />
        <h2
          className={`mt-8 max-w-[18ch] text-balance text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`}
          style={DISPLAY}
        >
          {title}
        </h2>

        <div className="mt-12 grid gap-y-10 md:mt-16 lg:grid-cols-2 lg:gap-x-16">
          {DUAL_CELLS.map((cell) => (
            <div key={cell.title} className="flex max-w-[36rem] flex-col">
              <p className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
                {cell.marker}
              </p>
              <p className="mt-3 max-w-[40ch] flex-1 text-[1.05rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]">
                {cell.body}
              </p>
              <div className="mt-7">
                <a
                  href={cell.href}
                  className={cell.variant === "primary" ? PRIMARY_ON_DARK : OUTLINE_ON_DARK}
                  {...(cell.variant === "outline"
                    ? { target: "_blank", rel: "noreferrer" }
                    : {})}
                >
                  {cell.title}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  CTA-007 · Contact card                                             */
/* ================================================================= */

const CONTACT_ROWS: ReadonlyArray<{
  label: string;
  value: string;
  href?: string;
}> = [
  { label: "Email", value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
  { label: "Studio", value: "Eccleston Yards, London" },
  { label: "Deploy", value: "Managed targets" },
];

function ContactCard() {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="w-full border border-[var(--rule)]"
      style={FRAME_STYLE}
    >
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-sm:flex-col max-sm:items-start max-sm:gap-1">
        <span
          id={titleId}
          className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
        >
          Get in touch
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Mercury Labs · London
        </strong>
      </div>

      <dl>
        {CONTACT_ROWS.map((row) => (
          <div
            key={row.label}
            className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-b border-dotted border-[var(--rule)]"
          >
            <dt className="flex items-center border-r border-dotted border-[var(--rule)] px-4 py-2.5 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">
              {row.label}
            </dt>
            <dd className="flex min-w-0 items-center px-4 py-2.5 text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[color-mix(in_srgb,var(--theme-ink)_92%,transparent)] uppercase">
              {row.href ? (
                <a
                  href={row.href}
                  className="break-all text-[var(--theme-accent-text)] underline-offset-4 hover:underline"
                >
                  {row.value}
                </a>
              ) : (
                row.value
              )}
            </dd>
          </div>
        ))}
      </dl>

      <div className="px-5 py-6 md:px-7">
        <p className="max-w-[42ch] text-[0.98rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
          Connect priority systems, review what MLX constructs and put the
          first approved business context to work in a focused two-week
          activation.
        </p>
        <div className="mt-6">
          <a href={WAITLIST_HREF} className={PRIMARY_ON_CREAM}>
            Discuss your first activation →
          </a>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  CTA-008 · Compact email capture                                    */
/* ================================================================= */

function CompactEmailCapture() {
  return (
    <div className="w-full" style={DISPLAY}>
      <span className={FIELD_LABEL_CLASS}>Work email</span>
      <div className="mt-2 flex gap-2 max-sm:flex-col">
        <span
          aria-hidden="true"
          className={`${INPUT_CLASS} block min-h-11 min-w-0 flex-1`}
        />
        <span className={`${PRIMARY_ON_CREAM} shrink-0`}>
          Request a conversation
        </span>
      </div>
      <p className="mt-3 text-[0.85rem] leading-[1.5] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]">
        We&apos;ll contact you to discuss a focused first activation.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  Group                                                              */
/* ================================================================= */

export const CTA_GROUP: ConceptGroup = {
  id: "cta",
  prefix: "CTA",
  title: "CTAs & forms",
  blurb:
    "Every route into the first-activation conversation: the closing band, the enquiry dialog and its inline and compact variants, plus the bars and cards that carry the same ask without the full frame.",
  specs: [
    {
      id: "CTA-001",
      title: "Closing section",
      note: "ClosingSection with the live home 07 content, dark band, accent disc, meta row.",
      surface: "dark",
      width: "full",
      render: () => <HomeClosing />,
    },
    {
      id: "CTA-002",
      title: "Inline enquiry form",
      note: "The waitlist form anatomy as an in-page visual study on cream; controls are deliberately noninteractive and there is no Close.",
      surface: "ground",
      width: "narrow",
      interaction: "static",
      render: () => <InlineEnquiryForm />,
    },
    {
      id: "CTA-003",
      title: "Waitlist dialog, in-page",
      note: "Local prototype of WaitlistDialog's 34rem frame: switch between default, error and success; submit advances to success and Close resets.",
      surface: "ground",
      width: "contained",
      interaction: "prototype",
      render: () => <WaitlistDialogReplica />,
    },
    {
      id: "CTA-004",
      title: "Dialog success state",
      note: "The same frame after submission, status marker, thanks copy and Close.",
      surface: "ground",
      width: "narrow",
      interaction: "static",
      render: () => <WaitlistSuccessReplica />,
    },
    {
      id: "CTA-005",
      title: "Sticky bar replica",
      note: "Hairline activation bar on a cream stage, rendered statically; marker, one line and the primary CTA right-aligned.",
      surface: "ground",
      width: "full",
      render: () => <StickyBarReplica />,
    },
    {
      id: "CTA-006",
      title: "Dual CTA band",
      note: "Dark band with two hairline-divided cells, activation (primary) and partnership (outline → partners page).",
      surface: "dark",
      width: "full",
      render: () => <DualCtaBand />,
    },
    {
      id: "CTA-007",
      title: "Contact card",
      note: "Exhibit-framed contact card with dotted ledger rows, from the About closing meta; primary → waitlist.",
      surface: "ground",
      width: "narrow",
      render: () => <ContactCard />,
    },
    {
      id: "CTA-008",
      title: "Compact email capture",
      note: "Single-row work-email capture with the primary action and a one-line footnote; static.",
      surface: "ground",
      width: "narrow",
      interaction: "static",
      render: () => <CompactEmailCapture />,
    },
  ],
};
